/**
 * ClientDashboard.BuyNowBanner.js
 * Banner flotante de compra con countdown de oferta y botón de pago Stripe.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Timer, Loader2 } from "lucide-react";
import { useCountdown } from "./ClientDashboard.Controls";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function BuyNowBanner({ token, account, siteSlug, offerExpiresAt, settings, modsExhausted }) {
  const [loading, setLoading] = useState(false);
  const secsLeft   = useCountdown(offerExpiresAt);
  const offerActive = secsLeft > 0 && !!offerExpiresAt;

  const price      = settings?.offer_price;
  const currency   = settings?.offer_currency || "UYU";
  const priceLabel = price ? `${currency} $${Number(price).toLocaleString("es-UY")}/mes` : "Ver precio";

  const fmtSecs = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}h ${m}m ${String(r).padStart(2, "0")}s`;
    return `${m}m ${String(r).padStart(2, "0")}s`;
  };

  const handleBuy = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          site_slug:    siteSlug,
          client_name:  account?.name  || "",
          client_email: account?.email || "",
          origin_url:   window.location.origin,
        }),
      });
      const d = await res.json();
      if (d.checkout_url) window.location.href = d.checkout_url;
      else toast.error(d.detail || "Error al iniciar el pago");
    } catch {
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`mx-0 flex-shrink-0 ${modsExhausted ? "ring-2 ring-red-500" : ""}`}
      style={{ background: "linear-gradient(135deg,#0f0000 0%,#1a0a00 60%,#0A0A0A 100%)" }}
    >
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <Timer size={14} className="text-red-400 flex-shrink-0 animate-pulse" />
          <div className="min-w-0">
            <p className="text-[11px] font-black text-white leading-tight truncate">
              {offerActive
                ? "Este sitio y nuestra oferta será eliminada en..."
                : "⚠ Este sitio será eliminado si no comprás"}
            </p>
            {offerActive && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[11px] font-black text-red-400 tabular-nums">{fmtSecs(secsLeft)}</span>
                <span className="text-[9px] text-gray-500 ml-0.5">— precio: {priceLabel}</span>
              </div>
            )}
            {!offerActive && (
              <p className="text-[9px] text-gray-500 mt-0.5">Comprá ahora y conservá tu sitio</p>
            )}
          </div>
        </div>

        <button
          onClick={handleBuy}
          disabled={loading || !siteSlug}
          data-testid="buy-now-banner-btn"
          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 bg-[#ffc700] text-black text-[11px] font-black rounded-xl hover:bg-yellow-400 transition-all active:scale-95 disabled:opacity-60"
        >
          {loading && <Loader2 size={11} className="animate-spin" />}
          {price ? priceLabel : "Comprar"}
        </button>
      </div>
    </div>
  );
}
